import { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import {
  dataTableLocales,
  dataTableSortIconSlots,
  getDataTableChromeSx,
} from "@/common/components/table/DataTable.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { useTranslation } from "@/translations";

import { buildChangeHistoryRows } from "./ChangeHistory.utils";
import { useChangeHistoryColumns } from "./hooks/useChangeHistoryColumns";
import { ChangeHistoryScope } from "./hooks/useChangeHistoryScope";
import { useClientChangeHistory } from "./hooks/useClientChangeHistory";

interface Props {
  clientId: string;
  scope: ChangeHistoryScope;
  onClose: () => void;
}

/**
 * "Änderungshistorie": a fixed-height dialog listing the field-level change history of what the
 * current client case file (Kundenakt) page shows — its section, or the single opened entry of
 * it — newest first, in a scrollable read-only table. Header and content spacing follow the app's
 * form dialogs (e.g. the appointment creation dialog).
 */
export function ChangeHistoryDialog({ clientId, scope, onClose }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { data: entries, isLoading } = useClientChangeHistory(clientId, scope);

  // Every row's values are formatted up front, so rebuilding them on an unrelated re-render would
  // redo a date or currency format per cell — and hand the grid a new row array each time.
  const rows = useMemo(() => buildChangeHistoryRows(entries ?? [], t, language), [entries, t, language]);
  const columns = useChangeHistoryColumns(rows);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth slotProps={{ paper: { sx: { height: "85vh" } } }}>
      <DialogTitle
        variant="h1"
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {t("changeHistory.dialog.title")}
        <IconButton aria-label={t("common.close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden", p: 5 }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            rowHeight={60}
            columnHeaderHeight={56}
            initialState={{ sorting: { sortModel: [{ field: "changedAtUtc", sort: "desc" }] } }}
            disableRowSelectionOnClick
            disableColumnSelector
            disableColumnMenu
            disableColumnResize
            hideFooter
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
            slots={dataTableSortIconSlots}
            localeText={dataTableLocales[language]}
            sx={(theme) => getDataTableChromeSx(theme)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
